/**
 * Builder.io Page Gate
 * 
 * Attempts to render Builder content for the current route.
 * Falls back to children (original route components) if no Builder content exists.
 */

import React, { Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import { useBuilderContent } from './useBuilderContent';
import { isBuilderEnabled } from './config';

// Lazy load BuilderComponent to keep it out of initial bundle
const BuilderComponent = lazy(() =>
  import('@builder.io/react').then((mod) => ({
    default: mod.BuilderComponent,
  }))
);

interface BuilderPageGateProps {
  children: React.ReactNode;
}

export function BuilderPageGate({ children }: BuilderPageGateProps) {
  const location = useLocation();
  const content = useBuilderContent(location.pathname);

  // If Builder is disabled, always render children
  if (!isBuilderEnabled()) {
    return <>{children}</>;
  }

  // If Builder content exists, render it
  if (content) {
    return (
      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        }
      >
        <BuilderComponent model="page" content={content.data} />
      </Suspense>
    );
  }

  // Fallback to original route components
  return <>{children}</>;
}

